import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import ZohoTargetModulePicker from '@/components/zoho/ZohoTargetModulePicker';
import { zohoAPI } from '@/lib/api';
import { toast } from 'sonner';
import { ArrowRight, X, Loader2, Save, AlertCircle, Wand2 } from 'lucide-react';

export default function ZohoFieldMapping({ form, modules, onMappingUpdated }) {
  const [mappings, setMappings] = useState(form.field_mappings || []);
  const [isActive, setIsActive] = useState(form.is_active || false);
  const [targetModule, setTargetModule] = useState(form.target_module || 'none');
  const [saving, setSaving] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [autoDetected, setAutoDetected] = useState(form.auto_detected_fields || {});

  const fields = form.detected_fields || [];

  const getMapping = (zohoField) => mappings.find(m => m.zoho_field === zohoField)?.app_field || '';

  const setMapping = (zohoField, appField) => {
    setDirty(true);
    const trimmed = appField?.trim();
    if (!trimmed) {
      setMappings(prev => prev.filter(m => m.zoho_field !== zohoField));
    } else {
      setMappings(prev => {
        const existing = prev.filter(m => m.zoho_field !== zohoField);
        return [...existing, { zoho_field: zohoField, app_field: trimmed, field_type: 'string' }];
      });
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const newStatus = mappings.length > 0 ? 'active' : (fields.length > 0 ? 'pending_mapping' : form.status);
      const res = await zohoAPI.updateForm(form.id, {
        field_mappings: mappings,
        is_active: isActive,
        status: newStatus,
        target_module: targetModule === 'none' ? null : targetModule,
      });
      toast.success('Mapping saved');
      if (res.data.penalties_backfilled > 0) {
        toast.success(`${res.data.penalties_backfilled} previously accepted submission(s) added to Penalties`);
      }
      setDirty(false);
      onMappingUpdated(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save mapping');
    }
    setSaving(false);
  };

  const handleAutoDetect = async () => {
    setDetecting(true);
    try {
      const res = await zohoAPI.autoDetectFields(form.id);
      const detected = res.data.detected_fields || {};
      setAutoDetected(detected);
      toast.success('Fields auto-detected: ' + Object.keys(detected).length + ' mappings found');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Auto-detect failed');
    }
    setDetecting(false);
  };

  // Build reverse map: field_name -> detected purpose
  const fieldPurposeMap = {};
  Object.entries(autoDetected).forEach(function(entry) {
    fieldPurposeMap[entry[1]] = entry[0];
  });

  if (fields.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground" data-testid="no-fields-message">
          <AlertCircle className="h-10 w-10 mx-auto mb-3 opacity-30" />
          <p className="font-medium">No fields detected yet</p>
          <p className="text-sm mt-1">Send a test submission via webhook, then click auto-detect.</p>
          <Button variant="outline" onClick={handleAutoDetect} disabled={detecting} className="mt-4" data-testid="auto-detect-empty-btn">
            {detecting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Wand2 className="h-4 w-4 mr-2" />}
            Auto-Detect Fields
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="field-mapping-section">
      {/* Controls */}
      <Card>
        <CardContent className="pt-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Switch
                checked={isActive}
                onCheckedChange={(val) => { setIsActive(val); setDirty(true); }}
                data-testid="form-active-switch"
              />
              <Label className="text-sm">Active</Label>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={handleAutoDetect} disabled={detecting} data-testid="auto-detect-btn">
                {detecting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Wand2 className="h-4 w-4 mr-2" />}
                Auto-Detect Fields
              </Button>
              <Button onClick={handleSave} disabled={saving || !dirty} data-testid="save-mapping-btn">
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Save Mapping
              </Button>
            </div>
          </div>
          <ZohoTargetModulePicker
            value={targetModule}
            onChange={(v) => { setTargetModule(v); setDirty(true); }}
            modules={modules}
          />
        </CardContent>
      </Card>

      {/* Field mapping rows */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">
            Field Mappings ({mappings.length}/{fields.length} mapped)
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="max-h-[400px] overflow-auto">
            {fields.map(zf => {
              const mapped = getMapping(zf);
              return (
                <div key={zf} className="flex items-center gap-3 px-4 py-2.5 border-b last:border-0 hover:bg-muted/30" data-testid={`field-row-${zf}`}>
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <code className="text-xs bg-muted px-2 py-1 rounded truncate font-mono">{zf}</code>
                    {fieldPurposeMap[zf] && (
                      <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200 shrink-0">{fieldPurposeMap[zf]}</Badge>
                    )}
                  </div>
                  <ArrowRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  <Input
                    className="w-[220px] h-8 text-sm"
                    placeholder="Map to field name..."
                    defaultValue={mapped}
                    onBlur={e => setMapping(zf, e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') setMapping(zf, e.target.value); }}
                    data-testid={`mapping-input-${zf}`}
                  />
                  {mapped && (
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 shrink-0" onClick={() => {
                      setMapping(zf, '');
                      // Clear the input visually
                      const input = document.querySelector(`[data-testid="mapping-input-${zf}"]`);
                      if (input) input.value = '';
                    }}>
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
